import React from "react";
import { Container } from "react-bootstrap";
import "../../styles/LegalDocuments.css";
import SEO from "../utils/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="legal-document">
      <SEO
        title="Privacy Policy - Sarv"
        description="Learn how Sarv collects, uses, and protects your personal information. Our comprehensive privacy policy covers data security, your rights, and GDPR/CCPA compliance."
        canonicalPath="/privacy-policy"
      />
      <Container className="legal-container">
        <h1 className="legal-title">Sarv Privacy Policy</h1>
        <div className="last-updated">Last Updated: June 5, 2026</div>
        
        <div className="intro-box">
          <p><strong>Welcome to Sarv!</strong> Your privacy is important to us. This Privacy Policy explains how M31-V1 LLC ("Sarv," "we," "us," or "our") collects, uses, shares, and protects your information when you use our mobile application, website, and services (collectively, the "Services").</p>
        </div>

        {/* Table of Contents */}
        <div className="toc" id="toc">
          <h3>Table of Contents</h3>
          <ul>
            <li><a href="#definitions">1. Key Definitions</a></li>
            <li><a href="#children">2. Children's Privacy</a></li>
            <li><a href="#collection">3. Information We Collect</a></li>
            <li><a href="#use">4. How We Use Your Information</a></li>
            <li><a href="#sharing">5. How We Share Your Information</a></li>
            <li><a href="#retention">6. Data Retention</a></li>
            <li><a href="#security">7. Data Security</a></li>
            <li><a href="#rights">8. Your Rights and Choices</a></li>
            <li><a href="#cookies">9. Cookies and Tracking Technologies</a></li>
            <li><a href="#transfers">10. International Data Transfers</a></li>
            <li><a href="#california">11. California Privacy Rights</a></li>
            <li><a href="#other-states">12. Other U.S. State Privacy Rights</a></li>
            <li><a href="#gdpr">13. EEA, UK & Switzerland Rights</a></li>
            <li><a href="#third-party">14. Third-Party Services</a></li>
            <li><a href="#changes">15. Changes to This Policy</a></li>
            <li><a href="#contact">16. Contact Us</a></li>
          </ul>
        </div>

        {/* 1. Key Definitions */}
        <section id="definitions">
          <h2>1. Key Definitions</h2>
          <p>To help you understand this policy, here are some key terms:</p>
          <ul>
            <li><strong>"Personal Information"</strong> means information that identifies, relates to, or could reasonably be linked with you, such as your name, email address, and health data.</li>
            <li><strong>"Sensitive Personal Information"</strong> means health data, account credentials, precise geolocation, racial or ethnic origin, religious beliefs, and similar information requiring special protection.</li>
            <li><strong>"De-Identified Information"</strong> means information that has been processed to remove personally identifying characteristics so it cannot reasonably be linked back to you.</li>
            <li><strong>"Cookies"</strong> means small data files stored on your device that help us recognize you and improve your experience.</li>
          </ul>
        </section>

        {/* 2. Children's Privacy */}
        <section id="children">
          <h2>2. Children's Privacy</h2>
          <div className="important-box">
            <p><strong>Important:</strong> Our Services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18.</p>
          </div>
          <p>If you are under 18 years old, please do not use our Services or provide any personal information to us. If we discover that we have collected personal information from a child under 18, we will delete that information as quickly as possible.</p>
          <p>If you believe we might have information from a child under 18, please contact us immediately at <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a>.</p>
        </section>

        {/* 3. Information We Collect */}
        <section id="collection">
          <h2>3. Information We Collect</h2>
          <p>We collect information from and about you in the following ways:</p>

          <h3>3.1 Information You Provide Directly</h3>
          <p>When you create an account, use our Services, or communicate with us, you may provide:</p>
          <ul>
            <li><strong>Account Information:</strong> A preferred name or nickname and your email address. We do <strong>not</strong> require your legal or full name — you may use a nickname — and we do <strong>not</strong> collect your date of birth. If you sign in with Apple or Google, we also receive the email address, name, and profile picture associated with that account.</li>
            <li><strong>Health Information:</strong> An age range (not your exact age or date of birth), gender, weight category, health goals, medical conditions, allergies, medications, symptoms, pregnancy or menopause status, mental-health and stress indicators, sleep patterns, dietary preferences, substance use (such as caffeine, alcohol, or smoking), exercise habits, and any wellness details you choose to share — including free-text descriptions and voice recordings when you use voice features</li>
            <li><strong>Subscription Information:</strong> When you subscribe through the Apple App Store or Google Play, the app store processes your payment and we never receive or store your credit card number, bank details, or billing address. If you subscribe through our website, a third-party payment processor handles your card and billing details on our behalf — we receive confirmation of your subscription and limited billing information, but do not store your full card number ourselves. We use this information only to manage your subscription.</li>
            <li><strong>Communications:</strong> Messages you send to us, chat conversations, feedback, and customer support inquiries</li>
            <li><strong>User-Generated Content:</strong> Photos, notes, journal entries, and other content you create or upload</li>
          </ul>

          <h3>3.2 Information Collected Automatically</h3>
          <p>When you use our Services, we automatically collect:</p>
          <ul>
            <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, mobile network information</li>
            <li><strong>Usage Data:</strong> Pages viewed, features used, actions taken, time spent, access times and dates</li>
            <li><strong>Location Data:</strong> General location (city, state, country) based on IP address; precise location if you grant permission</li>
            <li><strong>Log Data:</strong> IP address, browser type and version, time zone setting, crash reports, performance data</li>
          </ul>

          <h3>3.3 Information from Third-Party Sources</h3>
          <p>We may receive information about you from:</p>
          <ul>
            <li><strong>Analytics Providers:</strong> Usage patterns and demographic information</li>
            <li><strong>Advertising Partners:</strong> Information about ad performance and engagement</li>
            <li><strong>Payment Processors:</strong> Transaction confirmation and payment status</li>
          </ul>

          <h3>3.4 Information We Generate</h3>
          <p>We create or infer information based on your use of our Services:</p>
          <ul>
            <li><strong>Inferences:</strong> Your likely preferences, interests, health characteristics, and behavioral patterns</li>
            <li><strong>Recommendations:</strong> Personalized herb and recipe suggestions based on your health profile</li>
            <li><strong>Wellness Scores:</strong> Calculated metrics based on your health data and activities</li>
          </ul>
        </section>

        {/* 4. How We Use Your Information */}
        <section id="use">
          <h2>4. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>

          <div className="info-table">
            <div className="table-row">
              <div className="table-cell"><strong>Purpose</strong></div>
              <div className="table-cell"><strong>Types of Information Used</strong></div>
            </div>
            <div className="table-row">
              <div className="table-cell">
                <strong>Provide and Improve Our Services</strong><br/>
                • Deliver personalized health recommendations<br/>
                • Generate wellness scores and insights<br/>
                • Match you with herbs and recipes<br/>
                • Process your requests and transactions
              </div>
              <div className="table-cell">Account information, health information, usage data, device information, inferences</div>
            </div>
            <div className="table-row">
              <div className="table-cell">
                <strong>Personalization</strong><br/>
                • Customize your experience<br/>
                • Provide relevant content and features<br/>
                • Remember your preferences
              </div>
              <div className="table-cell">All categories of personal information including sensitive personal information</div>
            </div>
            <div className="table-row">
              <div className="table-cell">
                <strong>Communication</strong><br/>
                • Send service updates and notifications<br/>
                • Respond to your inquiries<br/>
                • Send marketing communications (with consent)<br/>
                • Provide customer support
              </div>
              <div className="table-cell">Contact information, account information, communications, usage data</div>
            </div>
            <div className="table-row">
              <div className="table-cell">
                <strong>Safety and Security</strong><br/>
                • Protect against fraud and abuse<br/>
                • Secure our systems and data<br/>
                • Verify identity and prevent unauthorized access<br/>
                • Comply with legal obligations
              </div>
              <div className="table-cell">Account information, device information, log data, location data, usage patterns</div>
            </div>
            <div className="table-row">
              <div className="table-cell">
                <strong>Research and Development</strong><br/>
                • Improve our algorithms and AI/ML models<br/>
                • Develop new features and services<br/>
                • Conduct data analysis and research<br/>
                • Test and optimize our platform
              </div>
              <div className="table-cell">De-identified or aggregated data from all categories</div>
            </div>
            <div className="table-row">
              <div className="table-cell">
                <strong>Analytics and Advertising</strong><br/>
                • Understand how users interact with our Services<br/>
                • Measure advertising effectiveness<br/>
                • Display relevant advertisements<br/>
                • Track conversions and attribution
              </div>
              <div className="table-cell">Device information, usage data, cookie data, inferences</div>
            </div>
            <div className="table-row">
              <div className="table-cell">
                <strong>Legal and Compliance</strong><br/>
                • Comply with laws and regulations<br/>
                • Enforce our Terms of Use<br/>
                • Respond to legal requests<br/>
                • Protect our rights and property
              </div>
              <div className="table-cell">All categories as necessary</div>
            </div>
          </div>

          <div className="info-box">
            <p><strong>Note on AI and Machine Learning:</strong> We use artificial intelligence and machine learning to analyze your health information and provide personalized recommendations. To do this, the relevant parts of your health profile, symptoms, and check-in content — and your audio when you use voice features — are sent to a third-party AI provider that processes the data on our behalf, solely to generate your results, and does not use it to train its own models. These automated processes help match you with relevant herbs, recipes, and wellness insights. We do <strong>not</strong> share this information with advertising platforms. You have the right to opt-out of automated decision-making in certain jurisdictions.</p>
          </div>
        </section>

        {/* 5. How We Share Your Information */}
        <section id="sharing">
          <h2>5. How We Share Your Information</h2>
          <p>We do not sell your personal information for money. However, we may share your information in the following circumstances:</p>

          <h3>5.1 Service Providers</h3>
          <p>We share information with third-party vendors who perform services on our behalf:</p>
          <ul>
            <li>Cloud hosting, database, and authentication</li>
            <li>Subscription management and payment processing (including the Apple App Store and Google Play, which handle the actual payment)</li>
            <li><strong>Artificial-intelligence processing</strong> to generate your personalized recommendations and insights, and to transcribe and read aloud voice interactions. To do this, the relevant parts of your health profile, symptoms, and check-in content — and your audio when you use voice features — are sent to this provider <strong>solely</strong> to produce your results, under contractual terms that require confidentiality and prohibit using your data to train its models.</li>
            <li>Crash and error diagnostics to keep the app stable</li>
            <li>Marketing attribution and measurement — see Section 5.3</li>
            <li>Customer support, email, and communication services</li>
            <li>Security and fraud prevention</li>
          </ul>
          <p>These service providers are contractually obligated to protect your information and use it only for the purposes we specify.</p>

          <h3>5.2 Advertising and Analytics Partners</h3>
          <p>We work with third-party advertising and analytics companies that collect information through cookies and similar technologies:</p>
          <ul>
            <li>Analytics providers (usage patterns and demographics)</li>
            <li>Social media advertising platforms (advertising and analytics)</li>
            <li>Other advertising networks and measurement partners</li>
          </ul>
          <p>These partners may combine information about you across different websites and apps. In some jurisdictions, this may be considered a "sale" or "sharing" of personal information. You can opt-out as described in Section 8.</p>

          <h3>5.3 Social Media Advertising</h3>
          <p>We use social media and other advertising platforms to show you relevant ads for Sarv and to measure how well our ads perform. To do this, we may share limited information with these platforms, including:</p>
          <ul>
            <li>Identifiers such as your email address (typically shared in a hashed, non-readable form)</li>
            <li>Device and advertising identifiers</li>
            <li>Information about your activity, such as whether you installed the app, created an account, or made a purchase</li>
          </ul>
          <p>These platforms use this information to match you to your existing account with them so we can reach you (and people with similar interests) with ads, and to tell us how our campaigns are doing. We do <strong>not</strong> share your health data or other sensitive personal information with advertising platforms for this purpose.</p>
          <div className="info-box">
            <p><strong>Your choices:</strong> In some jurisdictions this activity may be considered "sharing" or a "sale" of personal information. You can opt out of this advertising at any time — see Section 8.5. You can also manage ad preferences directly with each platform (for example, in your account settings with those platforms) and limit ad tracking in your device settings.</p>
          </div>

          <h3>5.4 With Your Consent</h3>
          <p>We share information when you direct us to do so, such as:</p>
          <ul>
            <li>Sharing your achievements on social media</li>
            <li>Participating in joint promotions or surveys</li>
          </ul>

          <h3>5.5 Business Transfers</h3>
          <p>If we are involved in a merger, acquisition, bankruptcy, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change and any choices you may have.</p>

          <h3>5.6 Legal Requirements</h3>
          <p>We may disclose your information when required by law or when we believe disclosure is necessary to:</p>
          <ul>
            <li>Comply with legal obligations, court orders, or subpoenas</li>
            <li>Respond to law enforcement or government requests</li>
            <li>Protect our rights, property, or safety</li>
            <li>Protect the rights, property, or safety of our users or the public</li>
            <li>Prevent or investigate fraud, security issues, or illegal activity</li>
            <li>Enforce our Terms of Use and other agreements</li>
          </ul>

          <h3>5.7 Aggregated and De-Identified Data</h3>
          <p>We may share aggregated, de-identified, or anonymized information that cannot reasonably be used to identify you for research, marketing, analytics, or any other purpose.</p>
        </section>

        {/* 6. Data Retention */}
        <section id="retention">
          <h2>6. Data Retention</h2>
          <p>We retain your personal information for as long as necessary to fulfill the purposes described in this policy, unless a longer retention period is required or permitted by law.</p>

          <h3>Retention Periods:</h3>
          <ul>
            <li><strong>Account Information:</strong> Retained while your account is active and for up to 3 years after account deletion (unless required longer for legal reasons)</li>
            <li><strong>Health Data:</strong> Retained while your account is active and for up to 5 years after account deletion (to support continuity of care if you return)</li>
            <li><strong>Transaction Records:</strong> Retained for 7 years for tax and accounting purposes</li>
            <li><strong>Communications:</strong> Retained for 3 years or until you request deletion</li>
            <li><strong>Marketing Data:</strong> Retained until you opt-out or request deletion</li>
            <li><strong>De-Identified Data:</strong> May be retained indefinitely</li>
          </ul>

          <p>When we delete your information, it will be removed from our active databases but may remain in backup systems for up to 90 days. We may retain certain information as required by law or for legitimate business purposes.</p>
        </section>

        {/* 7. Data Security */}
        <section id="security">
          <h2>7. Data Security</h2>
          <p>We implement reasonable physical, technical, and administrative security measures to protect your personal information from unauthorized access, use, disclosure, alteration, or destruction. These measures include:</p>

          <ul>
            <li><strong>Encryption:</strong> Data is encrypted in transit (TLS/SSL) and at rest (AES-256)</li>
            <li><strong>Access Controls:</strong> Strict authentication and authorization mechanisms</li>
            <li><strong>Monitoring:</strong> Regular security audits and continuous monitoring for threats</li>
            <li><strong>Employee Training:</strong> All employees receive privacy and security training</li>
            <li><strong>Data Minimization:</strong> We collect only the information necessary for our purposes</li>
            <li><strong>Secure Infrastructure:</strong> Our data is hosted on secure, compliant cloud platforms</li>
          </ul>

          <div className="warning-box">
            <p><strong>Important Security Notice:</strong> While we strive to protect your information, no method of transmission or storage is 100% secure. You use our Services at your own risk. Please report any security concerns to <a href="mailto:support@joinsarv.com">support@joinsarv.com</a> immediately.</p>
          </div>

          <p><strong>Your Responsibility:</strong> You are responsible for maintaining the confidentiality of your account credentials. Never share your password with anyone, and notify us immediately if you suspect unauthorized access to your account.</p>
        </section>

        {/* 8. Your Rights and Choices */}
        <section id="rights">
          <h2>8. Your Rights and Choices</h2>
          <p>Depending on where you live, you may have certain rights regarding your personal information:</p>

          <h3>8.1 Access and Portability</h3>
          <p>You have the right to:</p>
          <ul>
            <li>Access the personal information we have about you</li>
            <li>Request a copy of your information in a portable format</li>
            <li>View and download your data through your account settings</li>
          </ul>

          <h3>8.2 Correction and Update</h3>
          <p>You can:</p>
          <ul>
            <li>Update your profile information at any time in the app</li>
            <li>Request correction of inaccurate information</li>
            <li>Modify your health profile and preferences</li>
          </ul>

          <h3>8.3 Deletion</h3>
          <p>You can request deletion of your account and personal information by:</p>
          <ul>
            <li>Going to Settings → Delete Account in the app</li>
            <li>Contacting us at <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a></li>
          </ul>
          <p>Note: We may retain certain information as required by law or for legitimate business purposes (e.g., fraud prevention, resolving disputes).</p>

          <h3>8.4 Opt-Out of Marketing</h3>
          <p>You can opt-out of marketing communications by:</p>
          <ul>
            <li>Clicking "unsubscribe" in any marketing email</li>
            <li>Adjusting notification settings in the app</li>
            <li>Contacting us at <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a></li>
          </ul>
          <p>Note: You will still receive transactional emails (e.g., account notifications, receipts).</p>

          <h3>8.5 Opt-Out of Sale/Sharing and Targeted Advertising</h3>
          <p>You can opt-out of:</p>
          <ul>
            <li>The "sale" or "sharing" of your personal information (as defined by applicable privacy laws)</li>
            <li>Targeted advertising based on your activity</li>
            <li>Cross-context behavioral advertising</li>
          </ul>
          <p>To opt-out, email <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a>.</p>

          <h3>8.6 Limit Use of Sensitive Personal Information</h3>
          <p>In some jurisdictions, you can limit our use of sensitive personal information (such as health data) to only what is necessary to provide our Services. Contact us to exercise this right.</p>

          <h3>8.7 Object to Processing</h3>
          <p>You may have the right to object to certain processing of your information, including:</p>
          <ul>
            <li>Processing based on legitimate interests</li>
            <li>Direct marketing</li>
            <li>Automated decision-making</li>
          </ul>

          <h3>8.8 Non-Discrimination</h3>
          <p>We will never discriminate against you for exercising any of your privacy rights, including by:</p>
          <ul>
            <li>Denying goods or services</li>
            <li>Charging different prices or rates</li>
            <li>Providing a different level or quality of service</li>
          </ul>

          <h3>How to Exercise Your Rights</h3>
          <p>To exercise any of these rights, contact us at <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a> using the email address associated with your account. We will verify your identity before processing your request and respond within the timeframes required by applicable law (typically 30-45 days).</p>
        </section>

        {/* 9. Cookies and Tracking Technologies */}
        <section id="cookies">
          <h2>9. Cookies and Tracking Technologies</h2>
          <p>We use cookies, web beacons, pixels, and similar technologies to collect information and improve your experience.</p>

          <h3>9.1 Types of Cookies We Use</h3>
          <div className="info-table">
            <div className="table-row">
              <div className="table-cell"><strong>Cookie Type</strong></div>
              <div className="table-cell"><strong>Purpose</strong></div>
            </div>
            <div className="table-row">
              <div className="table-cell"><strong>Essential Cookies</strong></div>
              <div className="table-cell">Required for the Services to function (e.g., authentication, security)</div>
            </div>
            <div className="table-row">
              <div className="table-cell"><strong>Preference Cookies</strong></div>
              <div className="table-cell">Remember your settings and preferences</div>
            </div>
            <div className="table-row">
              <div className="table-cell"><strong>Analytics Cookies</strong></div>
              <div className="table-cell">Understand how users interact with our Services</div>
            </div>
            <div className="table-row">
              <div className="table-cell"><strong>Advertising Cookies</strong></div>
              <div className="table-cell">Deliver relevant ads and measure ad performance</div>
            </div>
          </div>

          <h3>9.2 Managing Cookies</h3>
          <p>You can control cookies through:</p>
          <ul>
            <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies</li>
            <li><strong>Opt-Out Tools:</strong> Visit <a href="http://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer">aboutads.info/choices</a> or <a href="http://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer">youronlinechoices.eu</a></li>
            <li><strong>Mobile Settings:</strong> Adjust tracking preferences in iOS or Android settings</li>
            <li><strong>Global Privacy Control:</strong> We honor GPC signals where required by law</li>
          </ul>

          <h3>9.3 Do Not Track</h3>
          <p>Some browsers have "Do Not Track" features. We do not currently respond to DNT signals, but we honor Global Privacy Control (GPC) signals where legally required.</p>
        </section>

        {/* 10. International Data Transfers */}
        <section id="transfers">
          <h2>10. International Data Transfers</h2>
          <p>Sarv is based in the United States, and your information may be processed and stored in the U.S. or other countries where our service providers operate. These countries may have data protection laws different from your country.</p>

          <p>When we transfer personal information from the EEA, UK, or Switzerland to other countries, we implement appropriate safeguards, including:</p>
          <ul>
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Adequacy decisions by the European Commission</li>
            <li>Your explicit consent</li>
          </ul>

          <p>By using our Services, you consent to the transfer of your information to the United States and other countries as described in this policy.</p>
        </section>

        {/* 11. California Privacy Rights */}
        <section id="california">
          <h2>11. California Privacy Rights (CCPA/CPRA)</h2>
          <p>If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA).</p>

          <h3>11.1 Notice at Collection</h3>
          <p>We collect the following categories of personal information:</p>
          <ul>
            <li>Identifiers (name, email, device IDs)</li>
            <li>Demographic information (age, gender)</li>
            <li>Commercial information (purchase history, preferences)</li>
            <li>Internet activity (browsing history, interactions)</li>
            <li>Geolocation data</li>
            <li>Sensory data (photos, audio if you provide them)</li>
            <li>Inferences (preferences, characteristics, behavior)</li>
            <li>Sensitive personal information (health data, account credentials, precise geolocation)</li>
          </ul>

          <h3>11.2 Sale and Sharing</h3>
          <div className="warning-box">
            <p><strong>Notice:</strong> We may "share" personal information for cross-context behavioral advertising, which may be considered a "sale" under California law. We do not sell the personal information of users we know are under 18.</p>
          </div>

          <h3>11.3 Your California Rights</h3>
          <p>California residents have the right to:</p>
          <ul>
            <li><strong>Know:</strong> What personal information we collect, use, disclose, and sell</li>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Delete:</strong> Request deletion of your personal information</li>
            <li><strong>Correct:</strong> Request correction of inaccurate information</li>
            <li><strong>Opt-Out:</strong> Opt-out of sale/sharing of your personal information</li>
            <li><strong>Limit:</strong> Limit use of your sensitive personal information</li>
            <li><strong>Non-Discrimination:</strong> Not be discriminated against for exercising your rights</li>
          </ul>

          <h3>11.4 Shine the Light Law</h3>
          <p>California Civil Code Section 1798.83 permits California residents to request information about disclosures of personal information to third parties for their direct marketing purposes. We do not share personal information with third parties for their own direct marketing purposes.</p>

          <h3>11.5 Authorized Agents</h3>
          <p>You may designate an authorized agent to make requests on your behalf. The agent must provide proof of authorization, and we may require you to verify your identity directly.</p>
        </section>

        {/* 12. Other U.S. State Privacy Rights */}
        <section id="other-states">
          <h2>12. Other U.S. State Privacy Rights</h2>

          <h3>12.1 Virginia, Colorado, Connecticut, Utah</h3>
          <p>If you are a resident of Virginia, Colorado, Connecticut, or Utah, you have rights similar to California residents, including:</p>
          <ul>
            <li>Right to confirm whether we process your personal data and access that data</li>
            <li>Right to correct inaccuracies</li>
            <li>Right to delete personal data</li>
            <li>Right to obtain a copy in a portable format</li>
            <li>Right to opt-out of targeted advertising, sale, and profiling</li>
          </ul>

          <h3>12.2 Washington My Health My Data Act</h3>
          <p>Washington residents have specific protections for consumer health data under the My Health My Data Act. For details, contact <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a>.</p>

          <h3>12.3 Nevada</h3>
          <p>Nevada residents may opt-out of the "sale" of personal information. We do not currently sell personal information as defined by Nevada law. If you have questions, contact <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a>.</p>
        </section>

        {/* 13. EEA, UK & Switzerland Rights (GDPR) */}
        <section id="gdpr">
          <h2>13. Rights for EEA, UK, and Switzerland Residents (GDPR)</h2>
          <p>If you are located in the European Economic Area (EEA), United Kingdom (UK), or Switzerland, you have specific rights under the General Data Protection Regulation (GDPR) and similar laws.</p>

          <h3>13.1 Legal Basis for Processing</h3>
          <p>We process your personal data based on the following legal grounds:</p>
          <ul>
            <li><strong>Consent:</strong> You have given clear consent for us to process your personal data for specific purposes</li>
            <li><strong>Contract:</strong> Processing is necessary to perform our contract with you (providing the Services)</li>
            <li><strong>Legal Obligation:</strong> Processing is necessary to comply with the law</li>
            <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate interests (e.g., fraud prevention, security, improving our Services) unless overridden by your rights</li>
          </ul>

          <h3>13.2 Your GDPR Rights</h3>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
            <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
            <li><strong>Restrict Processing:</strong> Request that we limit how we use your data</li>
            <li><strong>Data Portability:</strong> Receive your data in a machine-readable format</li>
            <li><strong>Object:</strong> Object to processing based on legitimate interests or for direct marketing</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent at any time (where processing is based on consent)</li>
            <li><strong>Complain:</strong> Lodge a complaint with your local supervisory authority</li>
          </ul>

          <h3>13.3 Data Protection Officer</h3>
          <p>For GDPR-related inquiries, contact us at <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a>.</p>

          <h3>13.4 Supervisory Authority</h3>
          <p>You have the right to lodge a complaint with a supervisory authority, particularly in the EEA member state of your habitual residence, place of work, or place of alleged infringement. A list of supervisory authorities is available at <a href="https://edpb.europa.eu/about-edpb/board/members_en" target="_blank" rel="noopener noreferrer">edpb.europa.eu</a>.</p>

          <h3>13.5 International Data Transfers</h3>
          <p>We transfer personal data from the EEA, UK, and Switzerland to the United States using:</p>
          <ul>
            <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
            <li>Adequacy decisions where available</li>
            <li>Other appropriate safeguards as required by law</li>
          </ul>
        </section>

        {/* 14. Third-Party Services */}
        <section id="third-party">
          <h2>14. Third-Party Services and Links</h2>
          <p>Our Services may contain links to third-party websites, apps, and services that we do not own or control. This Privacy Policy does not apply to those third-party services.</p>

          <div className="info-box">
            <p><strong>Important:</strong> We are not responsible for the privacy practices of third parties. When you click on links or connect to third-party services, you are subject to their privacy policies. We encourage you to read their privacy statements before providing any information.</p>
          </div>

          <h3>Third-Party Services We Integrate With:</h3>
          <ul>
            <li>Cloud hosting, database, and authentication providers</li>
            <li>Apple and Google (sign-in, and App Store / Google Play subscription billing)</li>
            <li>Subscription-management providers</li>
            <li>A third-party AI provider (AI processing of your inputs to generate recommendations, plus voice transcription and playback)</li>
            <li>Advertising and analytics partners (advertising measurement and attribution — see Section 5.3)</li>
            <li>Crash- and error-diagnostics providers</li>
          </ul>

          <p>When you connect your Sarv account to third-party services, you authorize us to access certain information from those services as permitted by their APIs and your privacy settings. We will only use this information as described in this policy.</p>
        </section>

        {/* 15. Changes to This Policy */}
        <section id="changes">
          <h2>15. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.</p>

          <p>When we make changes:</p>
          <ul>
            <li>We will update the "Last Updated" date at the top of this policy</li>
            <li>We will post the revised policy on our website and in the app</li>
            <li>For material changes, we may provide additional notice (e.g., email notification, in-app notification, or prominent notice on our website)</li>
            <li>We may require you to accept the new policy to continue using our Services</li>
          </ul>

          <p>We encourage you to review this policy periodically. Your continued use of our Services after changes are posted constitutes your acceptance of the updated policy.</p>
        </section>

        {/* 16. Contact Us */}
        <section id="contact">
          <h2>16. Contact Us</h2>
          <div className="contact-info-box">
            <p><strong>Questions, comments, or concerns about this Privacy Policy or our privacy practices?</strong></p>
            <p>We're here to help!</p>
            <p><strong>Email:</strong> <a href="mailto:support@joinsarv.com">support@joinsarv.com</a></p>
            <p><strong>Privacy Inquiries:</strong> <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a></p>
            <p><strong>GDPR Inquiries:</strong> <a href="mailto:privacy@joinsarv.com">privacy@joinsarv.com</a></p>
          </div>

          <div className="success-box">
            <p><strong>Thank you for trusting Sarv with your health information!</strong> We are committed to protecting your privacy and being transparent about our practices. If you have any questions or concerns, please don't hesitate to reach out.</p>
          </div>
        </section>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
